/**
 * Booking categories — the first step of the /book flow.
 *
 * The guest picks a category, then a tour inside it. Three categories, matching
 * how the owner actually sells:
 *
 *   Central Park Tours   the everyday park rides
 *   City + Park Tours    routes that leave the park for the avenues
 *   Holiday Tours        seasonal rides (the Christmas routes)
 *
 * HOW MEMBERSHIP IS DECIDED
 * -------------------------
 * Only the exceptions are listed explicitly (`tourIds`); every other bookable
 * tour falls into the default bucket, Central Park Tours.
 *
 * That direction matters. A hardcoded list of ALL tours would mean any tour the
 * owner adds in the admin panel tomorrow belongs to no category and silently
 * becomes unbookable — a booking page that quietly hides a product is worse than
 * one that puts it in a slightly wrong bucket. With a default bucket, a new tour
 * shows up immediately and can be re-categorised later.
 *
 * The Christmas tours are `is_visible = 0` in D1 because they must not appear in
 * the main /tours listing — they have their own campaign page. That flag is
 * "hide from the catalogue", not "do not sell", so /book deliberately loads them
 * (see fetchBookableTours) and surfaces them under Holiday. A hidden tour that no
 * category claims stays excluded, which is exactly how the retired Valentine tour
 * disappears without deleting its row.
 */

import { CHRISTMAS_TOUR_IDS } from './constants';
import { readCatalogSnapshot } from './catalogSnapshot';

/** Rockefeller Royal Lights Ride — leaves the park for the avenues. */
const CITY_PARK_TOUR_IDS = [25];

/**
 * The three Christmas variants.
 *
 * The standalone Valentine's Day tour (32) is deliberately NOT here. Valentine's
 * Day is no longer sold as its own product — every tour runs that day and
 * complimentary chocolate comes with all of them (see VALENTINES_CHOCOLATE_NOTE
 * in ./constants). Tour 32 stays in D1 as is_visible = 0 so past bookings keep
 * resolving their tour name, but no customer-facing surface offers it.
 */
const HOLIDAY_TOUR_IDS = [...CHRISTMAS_TOUR_IDS.filter((id) => id !== 19)];

/**
 * Tours that must never appear in the /book flow, whatever their flags say.
 * 36 = Proposal Tour — sold through its own /proposals page, which collects a
 * proposal location the generic flow has no field for.
 */
// Nothing is excluded any more. The Proposal Tour used to be, because it needs
// a proposal location the generic flow could not collect; /book now asks for
// that location (see DateTimeStep), so the tour sells here like any other.
export const EXCLUDED_FROM_BOOKING_IDS = [];

export const DEFAULT_CATEGORIES = [
  {
    id: 'central-park-tours',
    emoji: '🎠',
    name: 'Central Park Tours',
    tagline: 'All dates & times',
    description:
      'Our classic rides through Central Park — from a short loop to the full unhurried tour, with photo stops along the way.',
    isDefault: true,
    tourIds: null, // everything not claimed by another category
  },
  {
    id: 'city-park-tours',
    emoji: '🌃',
    name: 'City + Park Tours',
    tagline: 'Evenings · 8 PM onward',
    description:
      'Central Park paired with Manhattan’s lit avenues — past the Plaza and toward Rockefeller Center after dark.',
    tourIds: CITY_PARK_TOUR_IDS,
  },
  {
    id: 'holiday-tours',
    emoji: '❄️',
    name: 'Holiday Tours',
    tagline: 'Christmas season',
    description:
      'Seasonal rides — our Christmas routes through a lit-up December.',
    tourIds: HOLIDAY_TOUR_IDS,
  },
];

/**
 * Settings key holding the owner's category list.
 *
 * Categories used to be a hardcoded array. They now live in site_settings as
 * JSON so the owner can add, rename, re-describe, re-order and retire them from
 * the admin panel without a deploy. The array below is the fallback used the
 * first time the page loads (and any time the setting is missing or corrupt),
 * which is why it must always stay valid on its own.
 */
export const CATEGORIES_SETTING_KEY = 'book_categories';

/** Legacy key: order-only list, honoured when the newer key is absent. */
export const CATEGORY_ORDER_SETTING_KEY = 'book_category_order';

/**
 * Apply a stored order to CATEGORIES.
 *
 * `stored` is whatever came back from site_settings — a JSON array of category
 * ids, or junk. Anything unrecognised is ignored and anything MISSING from the
 * stored list is appended rather than dropped, so a category the owner never
 * ranked (or one added in a later release) still reaches the booking page.
 * Losing a category silently would cost bookings; showing it last will not.
 */
export function orderCategories(stored) {
  let ids = [];
  if (Array.isArray(stored)) {
    ids = stored;
  } else if (typeof stored === 'string' && stored.trim()) {
    try {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) ids = parsed;
    } catch {
      ids = [];
    }
  }
  const base = getCategories();
  const known = ids.filter((id) => base.some((c) => c.id === id));
  const ranked = known.map((id) => base.find((c) => c.id === id));
  const rest = base.filter((c) => !known.includes(c.id));
  return [...ranked, ...rest];
}

/**
 * Thumbnail for a category card.
 *
 * Categories have no image column of their own, so we show the first tour's
 * photo. That means the owner changes it the same way he changes any tour photo,
 * and the guest sees the same picture on the next step instead of a stock map
 * that matches nothing.
 */
export function categoryImage(toursInThisCategory, category) {
  // The owner's own picture wins. Before this the card always borrowed the
  // first tour's photo, so the only way to change what /book shows was to
  // re-order tours or re-upload a tour photo — which changed the tour page
  // too. An explicit image on the category is editable in Booking
  // Categories and affects nothing else.
  if (category && category.image) return category.image;
  const first = (toursInThisCategory || []).find((t) => t && t.photo);
  return first ? first.photo : null;
}

/**
 * Route map for a tour.
 *
 * The Christmas variants have purpose-drawn route maps in /images (the same
 * three the Christmas campaign page uses), so those are exact. Every other tour
 * falls back to its first photo, which for the park tours IS the green route
 * map the owner uploaded. If a tour ever shows the wrong picture here, the fix
 * is to upload the map as that tour's first photo — no code change needed.
 */
const CHRISTMAS_ROUTE_MAPS = {
  29: '/images/christmas-map-central-park.webp',
  37: '/images/christmas-map-holiday-avenue.webp',
  38: '/images/christmas-map-rockefeller.webp',
};

export function tourMapImage(tour) {
  if (!tour) return null;
  const id = parseInt(tour.id, 10);
  return CHRISTMAS_ROUTE_MAPS[id] || tour.photo || (id === 36 ? '/images/proposal-route-map.webp' : null);
}

/**
 * The icon is optional, and "no icon" has to actually mean no icon.
 *
 * Asked to clear it, people type a dash rather than emptying the field — which
 * is exactly what happened here: all three live categories were saved with
 * `emoji: "-"` and rendered as "-Central Park Tours". Treat the usual stand-ins
 * for nothing as nothing, so a cleared field stays cleared no matter how the
 * owner clears it.
 */
const NO_ICON = new Set(['', '-', '--', '–', '—', 'none', 'yok', 'n/a']);
const normaliseIcon = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return NO_ICON.has(trimmed.toLowerCase()) ? '' : trimmed;
};

/**
 * Turn whatever is stored in site_settings into a usable category list.
 *
 * This is the boundary between admin-entered data and the booking page, so it
 * is deliberately forgiving: anything malformed falls back to the code defaults
 * rather than rendering a broken or empty page.
 *
 * One rule is enforced, not merely hoped for: exactly one category must be the
 * DEFAULT bucket (tourIds empty). That bucket is what catches any tour the owner
 * has not filed anywhere — without it, adding a tour in the admin panel would
 * make it silently unbookable. If the saved list has none, the first entry is
 * promoted; if it has several, only the first keeps the flag.
 */
export function parseCategories(stored) {
  let raw = stored;
  if (typeof raw === 'string') {
    if (!raw.trim()) return DEFAULT_CATEGORIES;
    try { raw = JSON.parse(raw); } catch { return DEFAULT_CATEGORIES; }
  }
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_CATEGORIES;

  const cleaned = raw
    .filter((c) => c && typeof c === 'object' && typeof c.id === 'string' && c.id.trim() && c.name)
    .map((c) => ({
      id: c.id.trim(),
      emoji: normaliseIcon(c.emoji),
      image: typeof c.image === 'string' ? c.image.trim() : '',
      name: String(c.name),
      tagline: typeof c.tagline === 'string' ? c.tagline : '',
      description: typeof c.description === 'string' ? c.description : '',
      isDefault: c.isDefault === true,
      // Optional discount. The category does not hold a percentage of its own —
      // it names a coupon, and the coupon carries the real terms (percentage or
      // fixed, valid dates, which tours, usage cap). That matters because the
      // worker re-applies that same coupon when it prices the booking, so the
      // discount is enforced server-side and cannot be forged from the browser.
      couponCode: typeof c.couponCode === 'string' ? c.couponCode.trim().toUpperCase() : '',
      tourIds: Array.isArray(c.tourIds)
        ? c.tourIds.map((n) => parseInt(n, 10)).filter((n) => Number.isFinite(n))
        : [],
    }));

  if (cleaned.length === 0) return DEFAULT_CATEGORIES;

  // Drop duplicate ids — two categories answering to the same URL would make
  // getCategory ambiguous.
  const seen = new Set();
  const unique = cleaned.filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)));

  const defaults = unique.filter((c) => c.isDefault);
  if (defaults.length === 0) {
    unique[0].isDefault = true;
    unique[0].tourIds = [];
  } else if (defaults.length > 1) {
    let kept = false;
    unique.forEach((c) => {
      if (!c.isDefault) return;
      if (kept) c.isDefault = false;
      else kept = true;
    });
  }
  // The default bucket keeps ANY explicit picks it was given. It is a catch-all
  // *in addition to* its own list, not instead of it — that is what lets a tour
  // sit in a sale category and still appear under its normal one.

  return unique;
}

/**
 * The category list currently in force.
 *
 * Starts as the owner's list published with the build (the code defaults when
 * the build has none), so pages outside /book file tours the way /book will, and
 * is replaced once the admin's saved list arrives (see setActiveCategories,
 * called by BookPage). Keeping it in one place means getCategory/toursInCategory/
 * categoryForTour all answer consistently no matter which list is live.
 */
let ACTIVE_CATEGORIES = parseCategories(readCatalogSnapshot().categories);

export const getCategories = () => ACTIVE_CATEGORIES;

export function setActiveCategories(list) {
  ACTIVE_CATEGORIES = Array.isArray(list) && list.length ? list : DEFAULT_CATEGORIES;
  return ACTIVE_CATEGORIES;
}

/** Look up a category by its URL id. */
export const getCategory = (categoryId) =>
  ACTIVE_CATEGORIES.find((c) => c.id === categoryId) || null;

/**
 * Which category does this tour belong to? Returns the category id.
 * Anything not explicitly claimed lands in the default bucket.
 */
export function categoryForTour(tour) {
  if (!tour) return null;
  const id = parseInt(tour.id, 10);
  const explicit = ACTIVE_CATEGORIES.find((c) => c.tourIds && c.tourIds.includes(id));
  if (explicit) return explicit.id;
  const fallback = ACTIVE_CATEGORIES.find((c) => c.isDefault);
  return fallback ? fallback.id : null;
}

/** The tours belonging to a category, in the order the API returned them. */
export function toursInCategory(tours, categoryId) {
  const category = getCategory(categoryId);
  if (!category) return [];
  // A tour may belong to several categories at once — the same ride can sit in
  // its normal category and in a sale category, which is exactly how a discount
  // section works. So membership is never exclusive.
  const picked = (category.tourIds || [])
    .map((id) => tours.find((t) => parseInt(t.id, 10) === id))
    .filter(Boolean);

  if (!category.isDefault) return picked;

  // The default category shows its own picks first, then every tour that has no
  // home elsewhere. That trailing set is the safety net: a tour added in the
  // admin panel tomorrow is bookable immediately, without anyone filing it.
  //
  // A DISCOUNT category (one carrying a coupon) is a shelf, not a home — putting
  // a tour on sale must not remove it from the full-price listing. Otherwise the
  // owner builds a sale around the park rides and, without touching the default
  // category, those rides quietly become purchasable ONLY at the sale price.
  const homed = ACTIVE_CATEGORIES
    .filter((c) => !c.isDefault && !c.couponCode)
    .flatMap((c) => c.tourIds || []);
  const orphans = tours.filter((t) => !homed.includes(parseInt(t.id, 10)));

  // `picked` may overlap `orphans` when the owner pinned a tour here explicitly.
  const seen = new Set(picked.map((t) => parseInt(t.id, 10)));
  return [...picked, ...orphans.filter((t) => !seen.has(parseInt(t.id, 10)))];
}

/**
 * Stable URL identifier for a tour.
 *
 * Most tours have a slug, but the Valentine's Day tour (32) has slug NULL in
 * D1, so a slug-only scheme could not address it at all. Fall back to a
 * synthetic `tour-<id>` that resolveTour() understands.
 */
export const tourParam = (tour) =>
  tour ? (tour.slug || `tour-${tour.id}`) : '';

/** The Christmas rides, by id: a reordered category list must not change what the season offers. */
export const christmasRidesIn = (tours) =>
  HOLIDAY_TOUR_IDS.map((id) => tours.find((t) => parseInt(t.id, 10) === id)).filter(Boolean);

/** /book straight at this tour's date step (owner 2026-11-01: a tour's Book button opens that tour). */
export const bookTourPath = (tour) =>
  tour ? `/book/${categoryForTour(tour)}/${tourParam(tour)}` : '/book';

/** Resolve a `:tour` URL segment back to a tour row (slug or `tour-<id>`). */
export function resolveTour(tours, param) {
  if (!param) return null;
  const bySlug = tours.find((t) => t.slug === param);
  if (bySlug) return bySlug;
  const m = param.match(/^tour-(\d+)$/);
  if (m) {
    const id = parseInt(m[1], 10);
    return tours.find((t) => parseInt(t.id, 10) === id) || null;
  }
  return null;
}
